/// <amd-module name="Artifacts/FeedSettings/hooks/AddUpstream" />
import {
  filterCurrentFeed,
  isViewAlreadyInUpstreamsForAllProtocols,
  subtractUpstreams,
} from "Artifacts/FeedSettings/helpers/UpstreamHelpers";
import {
  AddUpstreamFormAction,
  AddUpstreamFormState,
  IAddUpstreamsErrors,
  IAddUpstreamsSelections,
  resetFormAndSelectionState,
  useAddUpstreamForm,
} from "Artifacts/FeedSettings/hooks/AddUpstreamForm";
import * as Res from "Artifacts/FeedSettings/Resources";
import { IFeedSettings, IUpstreamSource } from "Artifacts/FeedSettings/types";
import { IProtocolService } from "Artifacts/Root/protocols/services/ProtocolService";
import { IProtocol } from "Artifacts/Root/protocols/types";
import { Feed, FeedView, FeedVisibility } from "Artifacts/Root/web-apis/contracts/Feed";
import * as React from "react";
import { useEffect, useState } from "react";
import { ServiceEndpoint } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import { ObservableValue } from "VSS/Core/Observable";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { useComponentContext } from "VSS/Platform/Layout";
import { IListBoxItem } from "VSSUI/ListBox";
import { IWizardItem, WizardItemState } from "VSSUI/Wizard";

export enum AddUpstreamStage {
  SelectType = 0,
  Configuration = 1,
}

interface UseAddUpstreamHook {
  formState: AddUpstreamFormState;
  wizardItems: IWizardItem[];
  dispatch: React.Dispatch<AddUpstreamFormAction>;
  selectedIndex: AddUpstreamStage;
  setSelectedIndex: (index: AddUpstreamStage) => void;
  protocols: IProtocol[];
  selections: IAddUpstreamsSelections;
  errors: IAddUpstreamsErrors;
  publicUpstreams: IListBoxItem<IUpstreamSource | null>[];
  feedItems: IListBoxItem<Feed>[];
  feedViewItems: IListBoxItem<FeedView>[];
  serviceConnectionItems: IListBoxItem<ServiceEndpoint>[];
  isServiceConnectionsFeatureEnabled: boolean;
  resetFormState: () => void;
}

export function useAddUpstream(
  defaultPublicUpstreams: IUpstreamSource[] | undefined,
  upstreams: IUpstreamSource[] | undefined,
  loadFeeds: () => void,
  loadFeedViews: (feed: Feed) => void,
  loadServiceConnections: () => void,
  isPublicFeed: boolean,
  feeds?: Feed[],
  feedViews?: FeedView[],
  settings?: IFeedSettings,
  serviceConnections?: ServiceEndpoint[],
): UseAddUpstreamHook {
  const { pageContext } = useComponentContext();
  const { formState, dispatch, selections, errors } = useAddUpstreamForm();
  const [isServiceConnectionsFeatureEnabled, setServiceConnectionsFeatureEnabled] = useState<
    boolean
  >(false);

  const [selectedIndex, setSelectedIndex] = useState<AddUpstreamStage>(AddUpstreamStage.SelectType);
  const [wizardItems] = useState<IWizardItem[]>(() => [
    {
      title: Res.AddUpstreamPanel_WizardStep_Type,
      itemState: new ObservableValue<WizardItemState>(WizardItemState.None),
    },
    {
      title: Res.AddUpstreamPanel_WizardStep_Configuration,
      itemState: new ObservableValue<WizardItemState>(WizardItemState.None),
    },
  ]);

  const protocols = Object.values(
    pageContext.getService<IProtocolService>("IProtocolService").getProtocolMapping(),
  );

  let publicUpstreams = [
    ...subtractUpstreams(defaultPublicUpstreams, upstreams).map<
      IListBoxItem<IUpstreamSource | null>
    >((upstream) => ({
      id: upstream.id as string, // public upstreams always have ID.
      text: `${upstream.name} (${upstream.location})`,
      data: upstream,
    })),
    { id: "custom", text: Res.AddUpstreamPanel_Custom, data: null },
  ];

  let filteredFeeds = filterCurrentFeed(feeds, settings);

  // Public feeds cannot have custom public upstreams or internal private feeds as upstreams.
  // They can only have other public feeds or the public repositories.
  if (isPublicFeed) {
    publicUpstreams = publicUpstreams.filter((upstream) => upstream.id !== "custom");
    filteredFeeds = filteredFeeds.filter((feed) => feed.project?.visibility === "Public");
  }

  const feedItems = filteredFeeds.map<IListBoxItem<Feed>>((feed) => ({
    id: feed.id,
    text: feed.name,
    data: feed,
  }));

  const feedViewItems = (feedViews || []).map<IListBoxItem<FeedView>>((view) => ({
    id: view.id,
    text: view.name,
    data: view,
    disabled:
      isViewAlreadyInUpstreamsForAllProtocols(view, protocols, upstreams) ||
      (formState.selectedSameOrgFeed?.project?.visibility !== "Public" &&
        view.visibility === FeedVisibility.Private),
  }));
  const serviceConnectionItems = (serviceConnections || []).map<IListBoxItem<ServiceEndpoint>>(
    (connection) => ({ id: connection.id, text: connection.name, data: connection }),
  );

  useEffect(() => {
    formState.selectedSameOrgFeed && loadFeedViews(formState.selectedSameOrgFeed);
    selections.feedViewDropdown.clear();
  }, [formState.selectedSameOrgFeed, loadFeedViews, selections.feedViewDropdown]);

  // Preload list of feeds from server
  useEffect(() => {
    loadFeeds();
  }, [loadFeeds]);

  // Preload service connections.
  useEffect(() => {
    const isServiceConnectionsFeatureEnabled = isFeatureFlagEnabled(
      pageContext,
      "Packaging.Feed.Upstreams.SupportServiceEndpoint",
    );
    setServiceConnectionsFeatureEnabled(isServiceConnectionsFeatureEnabled);

    isServiceConnectionsFeatureEnabled && loadServiceConnections();
  }, [pageContext, loadServiceConnections]);

  const resetFormState = () => {
    resetFormAndSelectionState(dispatch, selections);
  };

  return {
    formState,
    wizardItems,
    dispatch,
    protocols,
    selectedIndex,
    setSelectedIndex,
    selections,
    feedItems,
    feedViewItems,
    publicUpstreams,
    errors,
    serviceConnectionItems,
    isServiceConnectionsFeatureEnabled,
    resetFormState,
  };
}
